import { useCallback } from "react";

import type { RefObject } from "react";

/**
 * Creates a memoized callback function that clears a timeout stored in a ref.
 *
 * @param timeoutRef - A ref object containing a timeout ID or null
 * @returns A memoized callback that clears the timeout and resets the ref to null
 *
 * @example
 * ```tsx
 * const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
 * const clearTimeoutCallback = useClearTimeoutRef(timeoutRef);
 *
 * // Set a timeout
 * timeoutRef.current = setTimeout(() => {}, 1000);
 *
 * // Clear it later
 * clearTimeoutCallback();
 * ```
 */
export const useClearTimeoutRef = (
  timeoutRef: RefObject<ReturnType<typeof setTimeout> | null>,
) =>
  useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [timeoutRef]);
